import React, { Component, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Stack,
  Chip,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getAppInsights } from '../../Services/Global';
import logger from '../../Services/LoggingService';

interface FeatureErrorInfo {
  componentStack: string;
  errorBoundary?: string;
  feature?: string;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: FeatureErrorInfo | null;
  errorId: string;
  dismissed: boolean;
}

interface FeatureErrorBoundaryProps {
  children: ReactNode;
  featureName: string;
  fallback?: ReactNode;
  allowDismiss?: boolean;
  onError?: (error: Error, errorInfo: FeatureErrorInfo) => void;
}

// Functional component for feature error display
const FeatureErrorDisplay: React.FC<{
  error: Error;
  errorInfo: FeatureErrorInfo;
  errorId: string;
  featureName: string;
  allowDismiss: boolean;
  onRetry: () => void;
  onDismiss?: () => void;
}> = ({ error, errorInfo, errorId, featureName, allowDismiss, onRetry, onDismiss }) => {
  const theme = useTheme();

  const styles = {
    container: {
      width: '100%',
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.error.light}`,
      borderRadius: theme.shape.borderRadius
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2)
    },
    errorIcon: {
      fontSize: 32,
      color: theme.palette.error.main,
      marginRight: theme.spacing(1)
    },
    titleSection: {
      display: 'flex',
      alignItems: 'center'
    },
    actionButtons: {
      marginTop: theme.spacing(2),
      gap: theme.spacing(1)
    },
    errorChip: {
      marginTop: theme.spacing(1)
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box sx={styles.titleSection}>
          <ErrorOutlineIcon sx={styles.errorIcon} />
          <Box>
            <Typography variant="h6" color="error" component="h3">
              {featureName} Error
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This feature encountered an error and couldn't load properly.
            </Typography>
          </Box>
        </Box>
        
        {allowDismiss && onDismiss && (
          <Button
            size="small"
            onClick={onDismiss}
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </Button>
        )}
      </Box>

      <Chip 
        label={`Error ID: ${errorId}`}
        variant="outlined"
        size="small"
        color="error"
        sx={styles.errorChip}
      />

      <Stack direction="row" spacing={1} sx={styles.actionButtons}>
        <Button
          variant="contained"
          color="primary"
          onClick={onRetry}
          startIcon={<RefreshIcon />}
          size="small"
        >
          Retry Feature
        </Button>
      </Stack>

      {process.env.NODE_ENV === 'development' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Development Error Details</AlertTitle>
          <Typography variant="body2" component="div">
            <strong>Feature:</strong> {featureName}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Error:</strong> {error.name}: {error.message}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

class FeatureErrorBoundary extends Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      dismissed: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<FeatureErrorBoundaryState> {
    const errorId = `FTR-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    
    return {
      hasError: true,
      error,
      errorId,
      dismissed: false
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorId = this.state.errorId;
    const featureName = this.props.featureName;
    
    logger.error(`Feature Error Boundary (${featureName}) caught an error`, error, {
      errorId: errorId,
      featureName: featureName,
      componentStack: errorInfo.componentStack
    }, {
      component: 'FeatureErrorBoundary',
      method: 'componentDidCatch'
    });

    const errorDetails: FeatureErrorInfo = {
      componentStack: errorInfo.componentStack || '',
      errorBoundary: 'FeatureErrorBoundary',
      feature: featureName
    };

    // Call onError callback if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorDetails);
      } catch (callbackError) {
        logger.error('Error in FeatureErrorBoundary onError callback', callbackError as Error, {
          featureName: featureName,
          errorId: errorId
        }, {
          component: 'FeatureErrorBoundary',
          method: 'componentDidCatch'
        });
      }
    }

    // Log to Application Insights
    try {
      const appInsights = getAppInsights();
      if (appInsights) {
        appInsights.trackException({
          exception: error,
          properties: {
            errorBoundary: 'FeatureErrorBoundary',
            featureName: featureName,
            errorId: errorId,
            componentStack: errorInfo.componentStack || '',
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href || 'unknown',
            userId: 'unknown'
          }
        });
        
        appInsights.trackEvent({
          name: 'ErrorBoundary:FeatureError',
          properties: {
            errorId: errorId,
            errorName: error.name,
            errorMessage: error.message,
            boundary: 'Feature',
            featureName: featureName,
            timestamp: new Date().toISOString()
          }
        });
      }
    } catch (loggingError) {
      logger.error('Failed to log feature error to Application Insights', loggingError as Error, {
        featureName: featureName,
        errorId: errorId
      }, {
        component: 'FeatureErrorBoundary',
        method: 'componentDidCatch'
      });
    }

    this.setState({
      errorInfo: errorDetails
    });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      dismissed: false
    });
  };

  handleDismiss = (): void => {
    this.setState({
      dismissed: true
    });
  };

  render(): ReactNode {
    // If dismissed, render nothing (hide the feature)
    if (this.state.dismissed) {
      return null;
    }

    if (this.state.hasError && this.state.error && this.state.errorInfo) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <FeatureErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          featureName={this.props.featureName}
          allowDismiss={this.props.allowDismiss || false}
          onRetry={this.handleRetry}
          onDismiss={this.props.allowDismiss ? this.handleDismiss : undefined}
        />
      );
    }

    return this.props.children;
  }
}

export default FeatureErrorBoundary; 